import { LS_THEME_PREFERENCE } from 'constants/local-storage';

import { DARK_COLORS, LIGHT_COLORS } from 'styles/style-constants';

export type ColorScheme = 'light' | 'dark';
export type ThemePreference = ColorScheme | 'system';

export const SYSTEM_THEME_QUERY = '(prefers-color-scheme: dark)';

export const getSystemTheme = (): ColorScheme =>
    typeof window !== 'undefined' && window.matchMedia?.(SYSTEM_THEME_QUERY).matches
        ? 'dark'
        : 'light';

export const parseThemePreference = (value: string | null): ThemePreference =>
    value === 'light' || value === 'dark' ? value : 'system';

export const getThemePreference = (): ThemePreference => {
    try {
        return parseThemePreference(window.localStorage.getItem(LS_THEME_PREFERENCE));
    } catch {
        return 'system';
    }
};

export const saveThemePreference = (preference: ThemePreference) => {
    try {
        window.localStorage.setItem(LS_THEME_PREFERENCE, preference);
    } catch {
        // Private browsing may deny storage; the choice still works for this session.
    }
};

export const resolveTheme = (preference: ThemePreference): ColorScheme =>
    preference === 'system' ? getSystemTheme() : preference;

export const getThemeColors = (theme: ColorScheme) =>
    theme === 'dark' ? DARK_COLORS : LIGHT_COLORS;

export const applyTheme = (theme: ColorScheme) => {
    const root = document.documentElement;
    const colors = getThemeColors(theme);
    root.dataset.theme = theme;
    root.style.colorScheme = theme;
    Object.entries(colors).forEach(([name, value]) => {
        root.style.setProperty(`--color-${name}`, value);
    });
    document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', colors.pageBackground);
};
